import React, { useMemo, useState, useEffect, createContext } from 'react';
import { useHttp, useSelectedFilterValues } from '@publicisgroupe/p-os-components';
import { webframeApi } from 'api/webframeApi';
import { POSLoader } from 'components/POSLoader/POSLoader';
import { ClientProfileTheme } from './types';
import { PROFILE_FILE_KEYS } from './constants';

interface ThemesProviderProps {
  children?: React.ReactNode;
}

export const ThemesContext = createContext<ClientProfileTheme | undefined>({});

export const ThemesProvider = (props: ThemesProviderProps) => {
  const http = useHttp();
  const { client: selectedClient } = useSelectedFilterValues();

  const [isLoading, setIsLoading] = useState(false);
  const [previousClient, setPreviousClient] = useState<string>();
  const [themesDataUrls, setThemesDataUrls] = useState<ClientProfileTheme>();

  useEffect(() => {
    const downloadClientProfile = async (client: string) => {
      const downloadClientProfileFile = async (fileType: string) => {
        try {
          const fileData = await webframeApi(http)?.downloadClientProfileFile(client, fileType);
          return fileData?.data ? window.URL.createObjectURL(fileData?.data) : '';
        } catch (error) {
          console.error(error);
          return '';
        }
      };

      const promises = Object.values(PROFILE_FILE_KEYS).map(downloadClientProfileFile);
      const urls = await Promise.all(promises);

      return Object.fromEntries(PROFILE_FILE_KEYS.map((key, index) => [key, urls[index]]));
    };

    if (selectedClient !== previousClient) {
      setIsLoading(true);
      setPreviousClient(selectedClient);

      downloadClientProfile(selectedClient)
        .then((themesDataUrls) => {
          setThemesDataUrls({ ...themesDataUrls, client: selectedClient });
        })
        .catch(() => setThemesDataUrls(undefined))
        .finally(() => setIsLoading(false));
    }
  }, [http, previousClient, themesDataUrls, selectedClient]);

  const themesContext = useMemo(() => themesDataUrls, [themesDataUrls]);

  return (
    <ThemesContext.Provider value={themesContext}>
      {isLoading && <POSLoader showLoadingText={false} loadingText="Updating Theme" />}
      {!isLoading && props.children}
    </ThemesContext.Provider>
  );
};
